import { useTranslation } from 'react-i18next'
import type { ValidatorFileEntry } from './ValidatorPage'
import { formatFileSize } from './validatorHelpers'

interface Props {
  entries: ValidatorFileEntry[]
  selectedId: string | null
  completedCount: number
  onSelect: (id: string) => void
  onReset: () => void
  emailSelectedIds: Set<string>
  onToggleEmailSelect: (id: string) => void
  onToggleSelectAll: () => void
  onSendEmail: () => void
}

function StatusIcon({ entry }: { entry: ValidatorFileEntry }) {
  if (entry.status === 'pending') {
    return (
      <span className="shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-400 text-[10px] font-bold flex items-center justify-center">
        –
      </span>
    )
  }
  if (entry.status === 'validating') {
    return (
      <svg className="animate-spin shrink-0 h-4 w-4 text-blue-500" viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
      </svg>
    )
  }
  if (entry.status === 'error') {
    return (
      <span className="shrink-0 w-5 h-5 rounded-full bg-red-100 text-red-600 text-[10px] font-bold flex items-center justify-center">
        !
      </span>
    )
  }
  const errors   = entry.result?.issues.filter(i => i.level === 'ERROR').length ?? 0
  const warnings = entry.result?.issues.filter(i => i.level === 'WARNING').length ?? 0
  if (errors > 0) {
    return (
      <span className="shrink-0 w-5 h-5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold flex items-center justify-center">
        ✗
      </span>
    )
  }
  if (warnings > 0) {
    return (
      <span className="shrink-0 w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold flex items-center justify-center">
        ⚠
      </span>
    )
  }
  return (
    <span className="shrink-0 w-5 h-5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold flex items-center justify-center">
      ✓
    </span>
  )
}

export function ValidatorFileSidebar({
  entries, selectedId, completedCount, onSelect, onReset,
  emailSelectedIds, onToggleEmailSelect, onToggleSelectAll, onSendEmail,
}: Props) {
  const { t } = useTranslation()

  const totalErrors   = entries.reduce((s, e) => s + (e.result?.issues.filter(i => i.level === 'ERROR').length ?? 0), 0)
  const totalWarnings = entries.reduce((s, e) => s + (e.result?.issues.filter(i => i.level === 'WARNING').length ?? 0), 0)
  const isValidating  = completedCount < entries.length

  const emailableEntries = entries.filter(e => e.status === 'done' && e.result?.saved_session_id != null)
  const emailSelectedCount = emailSelectedIds.size
  const allEmailableSelected = emailableEntries.length > 0 && emailableEntries.every(e => emailSelectedIds.has(e.id))

  return (
    <div className="w-60 shrink-0 flex flex-col border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800 self-start max-h-[calc(100vh-12rem)]">

      {/* Koptekst */}
      <div className="px-3 py-2.5 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-2 shrink-0">
        <div className="min-w-0">
          <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">
            {isValidating
              ? t('validator_file_sidebar.validating_progress', { completed: completedCount, total: entries.length })
              : t('validator_file_sidebar.file_count', { count: entries.length })}
          </div>
          {!isValidating && (
            <div className="text-[10px] mt-0.5 flex items-center gap-2">
              {totalErrors > 0 && (
                <span className="text-red-600 font-medium">{t('validator_file_sidebar.error_count', { count: totalErrors })}</span>
              )}
              {totalWarnings > 0 && (
                <span className="text-amber-600 font-medium">{t('validator_file_sidebar.warning_count', { count: totalWarnings })}</span>
              )}
              {totalErrors === 0 && totalWarnings === 0 && (
                <span className="text-green-600 font-medium">{t('validator_file_sidebar.all_valid')}</span>
              )}
            </div>
          )}
          {isValidating && (
            <div className="mt-1.5 w-full h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-300"
                style={{ width: `${Math.round((completedCount / entries.length) * 100)}%` }}
              />
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {emailableEntries.length > 0 && (
            <button
              onClick={() => onToggleSelectAll()}
              className="text-[10px] font-medium text-blue-500 hover:text-blue-700 px-1 py-0.5 rounded hover:bg-blue-50 transition-colors"
              title={allEmailableSelected ? t('validator_file_sidebar.deselect_all_tooltip') : t('validator_file_sidebar.select_all_tooltip')}
            >
              {allEmailableSelected ? t('validator_file_sidebar.deselect_all_button') : t('validator_file_sidebar.select_all_button')}
            </button>
          )}
          <button
            onClick={onReset}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
            title={t('validator_file_sidebar.reset_tooltip')}
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Bestandslijst */}
      <div className="overflow-y-auto flex-1">
        {entries.map(entry => {
          const isSelected = selectedId === entry.id
          const errors     = entry.result?.issues.filter(i => i.level === 'ERROR').length ?? 0
          const warnings   = entry.result?.issues.filter(i => i.level === 'WARNING').length ?? 0

          const isEmailable = entry.status === 'done' && entry.result?.saved_session_id != null
          const isEmailChecked = emailSelectedIds.has(entry.id)

          return (
            <div
              key={entry.id}
              className={`flex items-center border-b border-gray-100 dark:border-gray-800 last:border-0 ${
                isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-l-blue-500' : 'border-l-2 border-l-transparent'
              }`}
            >
              {isEmailable && (
                <button
                  onClick={e => { e.stopPropagation(); onToggleEmailSelect(entry.id) }}
                  className="pl-2 pr-1 py-2 shrink-0 text-gray-400 hover:text-blue-500 transition-colors"
                  title={isEmailChecked ? t('validator_file_sidebar.email_deselect_tooltip') : t('validator_file_sidebar.email_select_tooltip')}
                >
                  {isEmailChecked ? (
                    <svg className="w-3.5 h-3.5 text-blue-500" viewBox="0 0 16 16" fill="currentColor">
                      <rect x="1" y="1" width="14" height="14" rx="3" />
                      <path d="M4.5 8l2.5 2.5 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    </svg>
                  ) : (
                    <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="1.75" y="1.75" width="12.5" height="12.5" rx="2.25" />
                    </svg>
                  )}
                </button>
              )}
              {!isEmailable && <span className="pl-3" />}
              <button
                onClick={() => onSelect(entry.id)}
                className={`flex-1 text-left py-2 pr-3 flex items-center gap-2 transition-colors text-xs group ${
                  isEmailable ? '' : 'pl-3'
                }`}
              >
              <StatusIcon entry={entry} />

              <div className="min-w-0 flex-1">
                <div
                  className={`truncate font-medium leading-snug ${isSelected ? 'text-blue-800 dark:text-blue-300' : 'text-gray-800 dark:text-gray-200'}`}
                  title={entry.name}
                >
                  {entry.name}
                </div>
                {entry.status === 'done' && entry.result && (
                  <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                    {entry.result.standard} · {entry.result.document_type}
                  </div>
                )}
                {entry.status === 'pending' && (
                  <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{formatFileSize(entry.size)}</div>
                )}
                {entry.status === 'error' && (
                  <div className="text-[10px] text-red-500 mt-0.5 truncate" title={entry.errorMsg}>
                    {entry.errorMsg}
                  </div>
                )}
              </div>

              {entry.status === 'done' && errors > 0 && (
                <span className="shrink-0 text-[10px] bg-red-100 text-red-700 rounded-full px-1.5 py-0.5 font-semibold">
                  {errors}
                </span>
              )}
              {entry.status === 'done' && errors === 0 && warnings > 0 && (
                <span className="shrink-0 text-[10px] bg-amber-100 text-amber-700 rounded-full px-1.5 py-0.5 font-semibold">
                  {warnings}
                </span>
              )}
              </button>
            </div>
          )
        })}
      </div>

      {/* Verstuur-knop */}
      {emailableEntries.length > 0 && (
        <div className="px-3 py-2.5 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <button
            onClick={onSendEmail}
            disabled={emailSelectedCount === 0}
            className={`w-full py-1.5 text-xs font-semibold rounded-lg transition-colors ${
              emailSelectedCount > 0
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
            }`}
          >
            {emailSelectedCount > 0
              ? t('validator_file_sidebar.send_email_button', { count: emailSelectedCount })
              : t('validator_file_sidebar.send_email_button_disabled')}
          </button>
        </div>
      )}
    </div>
  )
}
